import { useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import { useTheme } from "../components/Theme";

const ORANGE = "#FF6B2B";

const STAGES = [
  { id: "prospeccao", label: "Prospecção", color: "#6366f1", emoji: "🎯" },
  { id: "contato",    label: "Contato",    color: "#f59e0b", emoji: "📞" },
  { id: "reuniao",    label: "Reunião",    color: "#3b82f6", emoji: "🤝" },
  { id: "proposta",   label: "Proposta",   color: "#8b5cf6", emoji: "📋" },
  { id: "fechado",    label: "Fechado",    color: "#10b981", emoji: "✅" },
  { id: "perdido",    label: "Perdido",    color: "#ef4444", emoji: "❌" },
];

const ORIGENS = ["Instagram","Google","Indicação","WhatsApp","Facebook","Site","Outro"];

const TIPOS_FU = [
  ["followup","📞 Follow-up"],["reuniao","🤝 Reunião"],
  ["ligacao","📱 Ligação"],["email","📧 Email"],["outro","📌 Outro"]
];

function fmt(v) { return v||v===0 ? `R$${Number(v).toLocaleString("pt-BR")}` : ""; }
function whatsappLink(lead) {
  const tel = lead.telefone?.replace(/\D/g,""); if(!tel) return null;
  const numero = tel.startsWith("55")?tel:`55${tel}`;
  const msg = `Olá ${lead.nome}! Tudo bem? 😊 Sou da Agência Optimus. Podemos conversar?`;
  return `https://wa.me/${numero}?text=${encodeURIComponent(msg)}`;
}
function followupStatus(lead) {
  if(!lead.followup?.data||lead.followup?.concluido) return null;
  const hoje=new Date();hoje.setHours(0,0,0,0);
  const d=new Date(lead.followup.data);d.setHours(0,0,0,0);
  if(d<hoje) return "atrasado"; if(d.getTime()===hoje.getTime()) return "hoje"; return "futuro";
}

// ── BADGE STAGE ──────────────────────────────────────────
function StageBadge({ stage, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const s = STAGES.find(x=>x.id===stage)||STAGES[0];
  useEffect(() => {
    const h = e => { if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ position:"relative", display:"inline-block" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ background:s.color, borderRadius:6, padding:"4px 12px", fontSize:12, fontWeight:600, color:"#fff", cursor:"pointer", whiteSpace:"nowrap" }}>
        {s.emoji} {s.label}
      </div>
      {open && (
        <div style={{ position:"absolute", top:"110%", left:0, zIndex:500, background:"#2C2C2F", border:"1px solid #3D3D42", borderRadius:12, padding:6, minWidth:180, boxShadow:"0 12px 40px rgba(0,0,0,.5)" }}>
          {STAGES.map(st=>(
            <div key={st.id} onClick={()=>{ onChange(st.id); setOpen(false); }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 10px", borderRadius:8, cursor:"pointer" }}
              onMouseEnter={ev=>ev.currentTarget.style.background=st.color+"18"}
              onMouseLeave={ev=>ev.currentTarget.style.background="transparent"}>
              <div style={{ width:10, height:10, borderRadius:3, background:st.color }}/>
              <span style={{ fontSize:13, color:"#F2F2F7", fontWeight:stage===st.id?600:400 }}>{st.emoji} {st.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── INLINE EDIT ──────────────────────────────────────────
function InlineEdit({ value, onChange, placeholder="—", style:s={} }) {
  const { theme } = useTheme();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value||"");
  const ref = useRef();
  useEffect(()=>{ setVal(value||""); },[value]);
  useEffect(()=>{ if(editing&&ref.current) ref.current.focus(); },[editing]);
  const commit=()=>{ setEditing(false); if(String(val)!==String(value||"")) onChange(val); };
  if(editing) return (
    <input ref={ref} value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{ if(e.key==="Enter")commit(); if(e.key==="Escape"){setVal(value||"");setEditing(false);} }}
      style={{ background:"transparent", border:"none", borderBottom:`1.5px solid ${ORANGE}`, outline:"none", color:theme.text, fontSize:13, width:"100%", padding:"2px 0", fontFamily:"'Inter',sans-serif", ...s }}/>
  );
  return (
    <div onClick={()=>setEditing(true)} style={{ cursor:"text", color:value?theme.text:theme.textMuted, fontSize:13, padding:"2px 4px", borderRadius:6, minWidth:30, ...s }}
      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,107,43,.08)"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      {value||<span style={{ color:theme.textMuted, fontSize:12 }}>{placeholder}</span>}
    </div>
  );
}

// ── MODAL LEAD ───────────────────────────────────────────
function Modal({ lead, onClose, onSave, onDelete }) {
  const { theme } = useTheme();
  const isNew = !lead?._id;
  const [form, setForm] = useState(lead || {
    nome:"", empresa:"", telefone:"", email:"",
    origem:"Instagram", pacote:"", valor:"",
    stage:"prospeccao", obs:"",
  });
  const [nota, setNota]   = useState("");
  const [aba, setAba]     = useState("dados");
  const [load, setLoad]   = useState(false);
  const [fuTipo, setFuTipo]     = useState(lead?.followup?.tipo||"followup");
  const [fuData, setFuData]     = useState(lead?.followup?.data?new Date(lead.followup.data).toISOString().slice(0,10):"");
  const [fuDesc, setFuDesc]     = useState(lead?.followup?.descricao||"");
  const [fuOk,   setFuOk]       = useState(lead?.followup?.concluido||false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const inp={ width:"100%", boxSizing:"border-box", background:theme.bgInput, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"9px 12px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name, fontFamily:"'Inter',sans-serif", transition:"border-color .15s" };

  const salvar=async()=>{
    if(!form.nome.trim()) return alert("Nome obrigatório");
    setLoad(true);
    try{ await onSave({...form, followup:fuData?{tipo:fuTipo,data:fuData,descricao:fuDesc,concluido:fuOk}:null}); }
    catch(e){ alert(e.response?.data?.erro||"Erro"); }
    finally{ setLoad(false); }
  };
  const addNota=async()=>{
    if(!nota.trim()||!lead?._id) return;
    await api.post(`/leads/${lead._id}/atividades`,{tipo:"nota",descricao:nota});
    setNota(""); onClose();
  };
  const waLink=whatsappLink(form);

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:20, padding:30, width:"100%", maxWidth:540, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,.6)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div>
            <div style={{ fontSize:11, color:ORANGE, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>LEAD</div>
            <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:theme.text }}>{form.nome||"Novo Lead"}</h2>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {!isNew&&waLink&&<a href={waLink} target="_blank" rel="noreferrer" style={{ background:"#25d36622", border:"1px solid #25d36644", borderRadius:8, padding:"6px 12px", color:"#25d366", fontSize:12, fontWeight:700, textDecoration:"none" }}>💬 WhatsApp</a>}
            <button onClick={onClose} style={{ background:theme.bgInput, border:"none", borderRadius:8, width:32, height:32, color:theme.textMuted, fontSize:18, cursor:"pointer" }}>×</button>
          </div>
        </div>

        {!isNew && (
          <div style={{ display:"flex", gap:4, marginBottom:16, borderBottom:`1px solid ${theme.border}`, paddingBottom:12 }}>
            {[["dados","📋 Dados"],["followup","⏰ Follow-up"],["historico","📝 Histórico"]].map(([id,label])=>(
              <button key={id} onClick={()=>setAba(id)} style={{ padding:"5px 12px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:`1px solid ${aba===id?ORANGE+"66":theme.border}`, background:aba===id?ORANGE+"22":"transparent", color:aba===id?ORANGE:theme.textMuted }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {(isNew||aba==="dados") && <>
          {/* Etapa */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:6, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Etapa</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {STAGES.map(s=>(
                <button key={s.id} onClick={()=>set("stage",s.id)} style={{ padding:"5px 14px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:`2px solid ${form.stage===s.id?s.color:theme.border}`, background:form.stage===s.id?s.color+"22":"transparent", color:form.stage===s.id?s.color:theme.textMuted }}>
                  {s.emoji} {s.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            {[["Nome *","nome"],["Empresa","empresa"],["Telefone (WhatsApp)","telefone"],["Email","email"]].map(([label,key])=>(
              <div key={key}>
                <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>{label}</label>
                <input value={form[key]||""} onChange={e=>set(key,e.target.value)} style={inp}
                  onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Origem</label>
              <select value={form.origem||"Instagram"} onChange={e=>set("origem",e.target.value)} style={{ ...inp, appearance:"auto" }}
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
                {ORIGENS.map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Serviço / Pacote</label>
              {/* Campo livre — sem opções fixas */}
              <input value={form.pacote||""} onChange={e=>set("pacote",e.target.value)} style={inp} placeholder="Ex: Gestão de redes..."
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            </div>
            <div>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Valor (R$)</label>
              <input type="number" value={form.valor||""} onChange={e=>set("valor",e.target.value)} style={inp} placeholder="0"
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            </div>
          </div>

          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Observações</label>
            <textarea value={form.obs||""} onChange={e=>set("obs",e.target.value)} rows={2} style={{ ...inp, resize:"vertical" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
        </>}

        {!isNew&&aba==="followup"&&<div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:8, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Tipo</label>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {TIPOS_FU.map(([id,label])=>(
                <button key={id} onClick={()=>setFuTipo(id)} style={{ padding:"5px 12px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:`1px solid ${fuTipo===id?ORANGE+"66":theme.border}`, background:fuTipo===id?ORANGE+"22":"transparent", color:fuTipo===id?ORANGE:theme.textMuted }}>{label}</button>
              ))}
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
            <div>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Data</label>
              <input type="date" value={fuData} onChange={e=>setFuData(e.target.value)} style={inp}
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            </div>
            <div>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:6, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Status</label>
              <div style={{ display:"flex", gap:8 }}>
                {[[false,"⏳ Pendente","#f59e0b"],[true,"✅ Concluído","#10b981"]].map(([val,lbl,color])=>(
                  <button key={String(val)} onClick={()=>setFuOk(val)} style={{ flex:1, padding:"7px 0", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer", border:`1.5px solid ${fuOk===val?color:theme.border}`, background:fuOk===val?color+"22":"transparent", color:fuOk===val?color:theme.textMuted }}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Descrição</label>
            <textarea value={fuDesc} onChange={e=>setFuDesc(e.target.value)} rows={3} style={{ ...inp, resize:"vertical" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
        </div>}

        {!isNew&&aba==="historico"&&<div>
          {lead?.atividades?.length>0?[...lead.atividades].reverse().map((a,i)=>(
            <div key={i} style={{ background:theme.bgInput, borderRadius:8, padding:"10px 12px", marginBottom:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ color:ORANGE, fontWeight:600, fontSize:12 }}>{a.nomeUsuario}</span>
                <span style={{ fontSize:11, color:theme.textMuted }}>{new Date(a.createdAt).toLocaleDateString("pt-BR")}</span>
              </div>
              <div style={{ fontSize:13, color:theme.textSub }}>{a.descricao}</div>
            </div>
          )):<p style={{ color:theme.textMuted, fontSize:13 }}>Nenhuma atividade ainda.</p>}
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <input value={nota} onChange={e=>setNota(e.target.value)} placeholder="Adicionar nota..." style={{ ...inp, flex:1 }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            <button onClick={addNota} style={{ background:ORANGE, border:"none", borderRadius:8, padding:"8px 14px", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700 }}>+</button>
          </div>
        </div>}

        <div style={{ display:"flex", gap:10, marginTop:16 }}>
          <button onClick={salvar} disabled={load} style={{ flex:1, background:`linear-gradient(135deg,${ORANGE},#FF9A5C)`, border:"none", borderRadius:12, padding:"13px 0", color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer", opacity:load?.7:1, boxShadow:`0 4px 14px ${ORANGE}44` }}>
            {load?"Salvando...":"Salvar"}
          </button>
          {!isNew&&waLink&&<a href={waLink} target="_blank" rel="noreferrer" style={{ background:"#25d36622", border:"1px solid #25d36644", borderRadius:12, padding:"13px 16px", color:"#25d366", fontWeight:700, fontSize:13, textDecoration:"none", display:"flex", alignItems:"center" }}>💬</a>}
          {!isNew&&<button onClick={()=>onDelete(lead._id)} style={{ background:"#ef444418", border:"1px solid #ef444433", borderRadius:12, padding:"13px 18px", color:"#ef4444", fontWeight:700, fontSize:13, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// ── NOVA LINHA ───────────────────────────────────────────
function NovaLinha({ stageId, onAdd, onCancel }) {
  const { theme } = useTheme();
  const [nome, setNome] = useState("");
  const ref = useRef();
  useEffect(()=>{ ref.current?.focus(); },[]);
  return (
    <tr style={{ background:theme.bgInput }}>
      <td style={{ padding:"7px 10px" }}><input type="checkbox" disabled/></td>
      <td colSpan={2} style={{ padding:"7px 14px" }}>
        <input ref={ref} value={nome} onChange={e=>setNome(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter"&&nome.trim()) onAdd(nome.trim(),stageId); if(e.key==="Escape") onCancel(); }}
          placeholder="Nome do lead... (Enter para adicionar)"
          style={{ background:"transparent", border:"none", borderBottom:`1.5px solid ${ORANGE}`, outline:"none", color:theme.text, fontSize:13, width:"100%", padding:"2px 0", fontFamily:"'Inter',sans-serif" }}/>
      </td>
      <td colSpan={5} style={{ padding:"7px 12px" }}>
        <span style={{ fontSize:11, color:theme.textMuted }}>↵ Enter · Esc para cancelar</span>
      </td>
    </tr>
  );
}

// ── COMPONENTE PRINCIPAL ─────────────────────────────────
export default function Kanban({ onStageChange, leadIdParaAbrir, currentUser }) {
  const { theme } = useTheme();
  const [leads,    setLeads]    = useState([]);
  const [modal,    setModal]    = useState(null);
  const [metricas, setMetricas] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [view,     setView]     = useState("lista");
  const [search,   setSearch]   = useState("");
  const [colapsados, setColapsados] = useState({});
  const [novaLinhaStage, setNovaLinhaStage] = useState(null);
  const [selected, setSelected] = useState([]);
  const [dragLeadId, setDragLeadId]     = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const carregarLeads    = useCallback(async()=>{ try{ const{data}=await api.get("/leads?tipo=lead"); setLeads(data.leads); }catch{} },[]);
  const carregarMetricas = useCallback(async()=>{ try{ const{data}=await api.get("/leads/metricas"); setMetricas(data); }catch{} },[]);

  useEffect(()=>{ Promise.all([carregarLeads(),carregarMetricas()]).finally(()=>setLoading(false)); },[]);

  useEffect(()=>{
    if(leadIdParaAbrir&&leads.length>0){ const l=leads.find(x=>x._id===leadIdParaAbrir); if(l) setModal(l); }
  },[leadIdParaAbrir,leads]);

  const salvar=async(form)=>{
    if(form._id){ const{data}=await api.put(`/leads/${form._id}`,form); setLeads(prev=>prev.map(l=>l._id===data._id?data:l)); }
    else{ const{data}=await api.post("/leads",form); setLeads(prev=>[data,...prev]); }
    setModal(null); carregarMetricas();
  };
  const excluir=async(id)=>{ await api.delete(`/leads/${id}`); setLeads(prev=>prev.filter(l=>l._id!==id)); setModal(null); carregarMetricas(); };
  const moverStage=async(leadId,novoStage)=>{
    const lead=leads.find(l=>l._id===leadId);
    if(!lead||lead.stage===novoStage) return;
    await api.patch(`/leads/${leadId}/stage`,{stage:novoStage});
    if(onStageChange) onStageChange(lead,lead.stage,novoStage);
    setLeads(prev=>prev.map(l=>l._id===leadId?{...l,stage:novoStage}:l));
    carregarMetricas();
  };
  const atualizarCampo=async(id,campo,valor)=>{
    try{ const{data}=await api.put(`/leads/${id}`,{[campo]:valor}); setLeads(prev=>prev.map(l=>l._id===id?{...l,...data}:l)); }catch{}
  };
  const adicionarLead=async(nome,stage)=>{
    try{
      const{data}=await api.post("/leads",{nome,tipo:"lead",stage,origem:"Instagram",valor:0});
      setLeads(prev=>[data,...prev]); setNovaLinhaStage(null);
    }catch(e){ alert(e.response?.data?.erro||"Erro"); }
  };

  const filtered=leads.filter(l=>{
    const q=search.toLowerCase();
    return !q||l.nome?.toLowerCase().includes(q)||l.empresa?.toLowerCase().includes(q);
  });

  const mrrFechado  = metricas?.pipeline?.find(p=>p._id==="fechado")?.valor||0;
  const mrrPipeline = metricas?.pipeline?.filter(p=>!["fechado","perdido"].includes(p._id)).reduce((a,p)=>a+p.valor,0)||0;

  const TH=({children,w,center})=>(
    <th style={{ padding:"10px 14px", fontSize:11, color:theme.textMuted, fontWeight:600, letterSpacing:".05em", textTransform:"uppercase", textAlign:center?"center":"left", borderRight:`1px solid ${theme.border}`, width:w, minWidth:w, background:theme.bgInput, whiteSpace:"nowrap" }}>{children}</th>
  );
  const TD=({children,w,center})=>(
    <td style={{ padding:"0 10px", height:44, borderRight:`1px solid ${theme.border}`, borderBottom:`1px solid ${theme.border}`, width:w, minWidth:w, textAlign:center?"center":"left", verticalAlign:"middle" }}>{children}</td>
  );

  if(loading) return <div style={{ color:theme.textMuted, padding:60, textAlign:"center", fontFamily:"'Inter',sans-serif", fontSize:16 }}>Carregando...</div>;

  // ── VISTA KANBAN ──────────────────────────────────────
  if(view==="kanban") return (
    <div style={{ fontFamily:"'Inter',sans-serif", background:theme.bg, minHeight:"100vh" }}>
      <div style={{ padding:"12px 24px", background:theme.bgSecond, borderBottom:`1px solid ${theme.border}`, display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
        {/* Toggle view */}
        <div style={{ display:"flex", background:theme.bgInput, borderRadius:10, padding:3, gap:2 }}>
          {[["lista","📄 Lista"],["kanban","📋 Kanban"]].map(([id,label])=>(
            <button key={id} onClick={()=>setView(id)} style={{ padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:view===id?700:400, cursor:"pointer", background:view===id?ORANGE:"transparent", border:"none", color:view===id?"#fff":theme.textMuted }}>
              {label}
            </button>
          ))}
        </div>
        <input placeholder="🔍 Buscar lead..." value={search} onChange={e=>setSearch(e.target.value)}
          style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, padding:"7px 12px", color:theme.text, fontSize:13, outline:"none", width:190, colorScheme:theme.name }}/>
        <div style={{ display:"flex", gap:16, marginLeft:8 }}>
          {[["Total",leads.length,theme.text],["MRR Fechado",fmt(mrrFechado),"#10b981"],["Pipeline",fmt(mrrPipeline),ORANGE]].map(([l,v,c])=>(
            <div key={l}><div style={{ fontSize:10, color:theme.textMuted }}>{l}</div><div style={{ fontSize:16, fontWeight:800, color:c, fontFamily:"'Inter',sans-serif" }}>{v}</div></div>
          ))}
        </div>
        <button onClick={()=>setModal({})} style={{ marginLeft:"auto", background:`linear-gradient(135deg,${ORANGE},#FF9A5C)`, border:"none", borderRadius:8, padding:"7px 18px", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>+ Novo Lead</button>
      </div>
      <div style={{ padding:"16px 24px 32px", overflowX:"auto" }}>
        <div style={{ display:"flex", gap:14, minWidth:"max-content" }}>
          {STAGES.map(stage=>{
            const sl=filtered.filter(l=>l.stage===stage.id);
            const mrr=sl.reduce((a,l)=>a+Number(l.valor||0),0);
            const isDragOver=dragOverStage===stage.id;
            return (
              <div key={stage.id} style={{ minWidth:220, maxWidth:240, flex:"0 0 220px" }}
                onDragOver={e=>{ e.preventDefault(); setDragOverStage(stage.id); }}
                onDragLeave={()=>setDragOverStage(null)}
                onDrop={e=>{ const id=e.dataTransfer.getData("leadId"); if(id) moverStage(id,stage.id); setDragOverStage(null); }}>
                <div style={{ background:isDragOver?stage.color+"30":stage.color+"18", border:`${isDragOver?2:1}px solid ${stage.color}${isDragOver?"":"33"}`, borderRadius:10, padding:"8px 12px", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", transition:"all .15s" }}>
                  <span style={{ fontSize:12, fontWeight:700, color:stage.color, fontFamily:"'Inter',sans-serif", letterSpacing:1 }}>{stage.emoji} {stage.label.toUpperCase()}</span>
                  <span style={{ background:stage.color+"33", color:stage.color, borderRadius:99, fontSize:11, fontWeight:700, padding:"1px 8px" }}>{sl.length}</span>
                </div>
                {mrr>0&&<div style={{ fontSize:11, color:theme.textMuted, marginBottom:8 }}>{stage.id==="fechado"?"✅ ":"💰 "}<span style={{ color:stage.color, fontWeight:700 }}>{fmt(mrr)}</span></div>}
                <div style={{ minHeight:60 }}>
                  {sl.map(lead=>{
                    const waLink=whatsappLink(lead);
                    const fuStatus=followupStatus(lead);
                    const isDrag=dragLeadId===lead._id;
                    return (
                      <div key={lead._id} draggable
                        onDragStart={e=>{ e.dataTransfer.setData("leadId",lead._id); setDragLeadId(lead._id); }}
                        onDragEnd={()=>setDragLeadId(null)}
                        style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:10, padding:"11px 13px", marginBottom:8, borderLeft:`3px solid ${stage.color}`, cursor:"grab", opacity:isDrag?.5:1, transition:"transform .15s", boxShadow:theme.name==="light"?"0 1px 4px rgba(0,0,0,.08)":"none" }}
                        onMouseEnter={e=>e.currentTarget.style.transform="translateY(-1px)"}
                        onMouseLeave={e=>e.currentTarget.style.transform="none"}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }} onClick={()=>setModal(lead)}>
                          <span style={{ fontWeight:700, color:theme.text, fontSize:13 }}>{lead.nome}</span>
                          <span style={{ color:ORANGE, fontWeight:800, fontSize:12, marginLeft:6 }}>{fmt(lead.valor)}</span>
                        </div>
                        {lead.empresa&&<div style={{ fontSize:11, color:theme.textMuted, marginBottom:6 }} onClick={()=>setModal(lead)}>{lead.empresa}</div>}
                        <div style={{ display:"flex", gap:5, flexWrap:"wrap", alignItems:"center", justifyContent:"space-between" }}>
                          <div style={{ display:"flex", gap:4 }} onClick={()=>setModal(lead)}>
                            {lead.origem&&<span style={{ background:"#6366f122", color:"#6366f1", border:"1px solid #6366f144", borderRadius:6, fontSize:10, fontWeight:700, padding:"2px 7px" }}>{lead.origem}</span>}
                            {fuStatus==="atrasado"&&<span style={{ background:"#ef444422", color:"#ef4444", border:"1px solid #ef444444", borderRadius:6, fontSize:10, fontWeight:700, padding:"2px 7px" }}>⚠️ Atrasado</span>}
                            {fuStatus==="hoje"&&<span style={{ background:ORANGE+"22", color:ORANGE, border:`1px solid ${ORANGE}44`, borderRadius:6, fontSize:10, fontWeight:700, padding:"2px 7px" }}>⏰ Hoje</span>}
                          </div>
                          {waLink&&<a href={waLink} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{ background:"#25d36618", border:"1px solid #25d36633", borderRadius:6, padding:"2px 8px", color:"#25d366", fontSize:11, fontWeight:700, textDecoration:"none" }}>💬</a>}
                        </div>
                      </div>
                    );
                  })}
                  {sl.length===0&&<div style={{ border:`1.5px dashed ${theme.border}`, borderRadius:10, padding:"18px 0", textAlign:"center", color:theme.border, fontSize:12 }}>Arraste aqui</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {modal!==null&&<Modal lead={Object.keys(modal).length?modal:null} onClose={()=>setModal(null)} onSave={salvar} onDelete={excluir}/>}
    </div>
  );

  // ── VISTA LISTA ───────────────────────────────────────
  return (
    <div style={{ fontFamily:"'Inter',sans-serif", background:theme.bg, minHeight:"100vh" }}>
      {/* Header */}
      <div style={{ padding:"16px 28px 0", background:theme.bgSecond, borderBottom:`1px solid ${theme.border}` }}>
        <div style={{ display:"flex", gap:24, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ display:"flex", gap:20 }}>
            {[["Total",leads.length,theme.text],["Pipeline",fmt(mrrPipeline),ORANGE],["Fechado",fmt(mrrFechado),"#10b981"]].map(([l,v,c])=>(
              <div key={l}><div style={{ fontSize:11, color:theme.textMuted, fontWeight:500 }}>{l}</div><div style={{ fontSize:20, fontWeight:800, color:c }}>{v}</div></div>
            ))}
          </div>
          <div style={{ marginLeft:"auto", display:"flex", gap:10, alignItems:"center" }}>
            {/* Toggle view */}
            <div style={{ display:"flex", background:theme.bgInput, borderRadius:10, padding:3, gap:2 }}>
              {[["lista","📄 Lista"],["kanban","📋 Kanban"]].map(([id,label])=>(
                <button key={id} onClick={()=>setView(id)} style={{ padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:view===id?700:400, cursor:"pointer", background:view===id?ORANGE:"transparent", border:"none", color:view===id?"#fff":theme.textMuted }}>
                  {label}
                </button>
              ))}
            </div>
            <input placeholder="🔍 Pesquisar lead..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{ background:theme.bgCard, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"8px 14px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name, width:200, fontFamily:"'Inter',sans-serif" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            <button onClick={()=>setModal({})} style={{ background:`linear-gradient(135deg,${ORANGE},#FF9A5C)`, border:"none", borderRadius:10, padding:"9px 18px", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", boxShadow:`0 4px 12px ${ORANGE}44` }}>
              + Novo Lead
            </button>
          </div>
        </div>
      </div>

      {/* Tabela agrupada por stage */}
      <div style={{ overflowX:"auto", padding:"16px 0 40px" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
          <thead>
            <tr>
              <TH w={36}><input type="checkbox" onChange={e=>setSelected(e.target.checked?leads.map(l=>l._id):[])}/></TH>
              <TH w={260}>Lead / Empresa</TH>
              <TH w={160} center>Etapa</TH>
              <TH w={120}>Origem</TH>
              <TH w={120}>Serviço</TH>
              <TH w={120}>Valor</TH>
              <TH w={130}>Contato</TH>
              <TH w={120}>Responsável</TH>
              <TH w={52} center>⋮</TH>
            </tr>
          </thead>

          {STAGES.map(stage=>{
            const stageLeads = filtered.filter(l=>l.stage===stage.id);
            const totalV     = stageLeads.reduce((a,l)=>a+Number(l.valor||0),0);
            const colapsado  = colapsados[stage.id];
            const isDragOver = dragOverStage===stage.id;

            return (
              <tbody key={stage.id}
                onDragOver={e=>{ e.preventDefault(); setDragOverStage(stage.id); }}
                onDragLeave={()=>setDragOverStage(null)}
                onDrop={e=>{ const id=e.dataTransfer.getData("leadId"); if(id) moverStage(id,stage.id); setDragOverStage(null); setDragLeadId(null); }}>

                {/* Header do grupo/stage */}
                <tr>
                  <td colSpan={9} style={{ padding:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:`3px solid ${stage.color}`, background:isDragOver?stage.color+"25":stage.color+"08", userSelect:"none", transition:"background .15s" }}>
                      <span onClick={()=>setColapsados(c=>({...c,[stage.id]:!c[stage.id]}))} style={{ color:stage.color, fontSize:14, cursor:"pointer", display:"inline-block", transform:colapsado?"rotate(-90deg)":"rotate(0deg)", transition:"transform .2s" }}>▾</span>
                      <span style={{ fontSize:16 }}>{stage.emoji}</span>
                      <span style={{ fontWeight:700, color:stage.color, fontSize:14 }}>{stage.label}</span>
                      <span style={{ background:stage.color+"25", color:stage.color, borderRadius:99, fontSize:11, fontWeight:700, padding:"2px 10px" }}>{stageLeads.length}</span>
                      {isDragOver&&<span style={{ fontSize:12, color:stage.color, fontWeight:600, background:stage.color+"22", borderRadius:6, padding:"2px 10px" }}>↓ Solte aqui</span>}
                      <span style={{ marginLeft:"auto", fontSize:13, color:theme.textMuted, fontWeight:600 }}>{fmt(totalV)}</span>
                    </div>
                  </td>
                </tr>

                {/* Linhas */}
                {!colapsado && stageLeads.map(lead=>{
                  const isSel  = selected.includes(lead._id);
                  const isDrag = dragLeadId===lead._id;
                  const waLink = whatsappLink(lead);
                  const fuStatus = followupStatus(lead);
                  return (
                    <tr key={lead._id} draggable
                      onDragStart={e=>{ e.dataTransfer.setData("leadId",lead._id); setDragLeadId(lead._id); }}
                      onDragEnd={()=>setDragLeadId(null)}
                      style={{ background:isSel?ORANGE+"08":isDrag?"rgba(255,107,43,.05)":theme.bgCard, transition:"background .1s", opacity:isDrag?.6:1, cursor:"grab" }}
                      onMouseEnter={e=>{ if(!isSel&&!isDrag) e.currentTarget.style.background=theme.bgHover; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background=isSel?ORANGE+"08":isDrag?"rgba(255,107,43,.05)":theme.bgCard; }}>

                      <TD w={36} center>
                        <input type="checkbox" checked={isSel} onChange={()=>setSelected(prev=>prev.includes(lead._id)?prev.filter(x=>x!==lead._id):[...prev,lead._id])} onClick={e=>e.stopPropagation()}/>
                      </TD>
                      <TD w={260}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ color:theme.border, fontSize:14, cursor:"grab", flexShrink:0 }} title="Arrastar para outro stage">⠿</span>
                          <div style={{ width:3, height:32, borderRadius:99, background:stage.color, flexShrink:0 }}/>
                          <div style={{ flex:1, overflow:"hidden" }}>
                            <InlineEdit value={lead.nome} placeholder="Nome do lead" onChange={v=>atualizarCampo(lead._id,"nome",v)} style={{ fontWeight:600 }}/>
                            {lead.empresa&&<div style={{ fontSize:11, color:theme.textMuted, paddingLeft:4 }}>{lead.empresa}</div>}
                          </div>
                          {fuStatus==="atrasado"&&<span style={{ fontSize:10, color:"#ef4444", fontWeight:700, flexShrink:0 }}>⚠️</span>}
                          {fuStatus==="hoje"&&<span style={{ fontSize:10, color:ORANGE, fontWeight:700, flexShrink:0 }}>⏰</span>}
                        </div>
                      </TD>
                      <TD w={160} center>
                        <StageBadge stage={lead.stage} onChange={v=>moverStage(lead._id,v)}/>
                      </TD>
                      <TD w={120}>
                        <span style={{ fontSize:12, color:theme.textSub }}>{lead.origem||"—"}</span>
                      </TD>
                      <TD w={120}>
                        <InlineEdit value={lead.pacote} placeholder="Serviço" onChange={v=>atualizarCampo(lead._id,"pacote",v)}/>
                      </TD>
                      <TD w={120}>
                        <InlineEdit value={lead.valor?fmt(lead.valor):""} placeholder="R$ 0" onChange={v=>atualizarCampo(lead._id,"valor",Number(v.replace(/\D/g,"")))} style={{ color:ORANGE, fontWeight:700 }}/>
                      </TD>
                      <TD w={130}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <InlineEdit value={lead.telefone} placeholder="Telefone" onChange={v=>atualizarCampo(lead._id,"telefone",v)}/>
                          {waLink&&<a href={waLink} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{ color:"#25d366", fontSize:14, flexShrink:0, textDecoration:"none" }}>💬</a>}
                        </div>
                      </TD>
                      <TD w={120}>
                        <span style={{ fontSize:12, color:theme.textMuted }}>{lead.nomeResponsavel||"—"}</span>
                      </TD>
                      <TD w={52} center>
                        <button onClick={()=>setModal(lead)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:20, borderRadius:6, padding:"2px 8px", width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center" }}
                          onMouseEnter={e=>{ e.currentTarget.style.background=theme.bgInput; e.currentTarget.style.color=theme.text; }}
                          onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=theme.textMuted; }}>⋮</button>
                      </TD>
                    </tr>
                  );
                })}

                {/* Nova linha */}
                {!colapsado&&novaLinhaStage===stage.id&&(
                  <NovaLinha stageId={stage.id} onAdd={adicionarLead} onCancel={()=>setNovaLinhaStage(null)}/>
                )}

                {/* Botão adicionar */}
                {!colapsado&&(
                  <tr>
                    <td colSpan={9} style={{ padding:"6px 14px", borderBottom:`1px solid ${theme.border}`, background:theme.bg }}>
                      <button onClick={()=>setNovaLinhaStage(stage.id)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:6, padding:"5px 8px", borderRadius:8, fontFamily:"'Inter',sans-serif" }}
                        onMouseEnter={e=>{ e.currentTarget.style.background=stage.color+"10"; e.currentTarget.style.color=stage.color; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=theme.textMuted; }}>
                        + Adicionar lead
                      </button>
                    </td>
                  </tr>
                )}

                {/* Total */}
                {!colapsado&&stageLeads.length>0&&(
                  <tr style={{ background:stage.color+"06" }}>
                    <td colSpan={5} style={{ padding:"8px 14px", fontSize:12, color:theme.textMuted, borderBottom:`1px solid ${theme.border}`, fontWeight:500 }}>{stageLeads.length} lead{stageLeads.length!==1?"s":""}</td>
                    <td style={{ padding:"8px 14px", fontSize:14, fontWeight:800, color:stage.color, borderBottom:`1px solid ${theme.border}` }}>{fmt(totalV)}</td>
                    <td colSpan={3} style={{ borderBottom:`1px solid ${theme.border}` }}/>
                  </tr>
                )}

                <tr><td colSpan={9} style={{ height:16, background:theme.bg }}/></tr>
              </tbody>
            );
          })}
        </table>
      </div>

      {modal!==null&&<Modal lead={Object.keys(modal).length?modal:null} onClose={()=>setModal(null)} onSave={salvar} onDelete={excluir}/>}
    </div>
  );
}
